import React, { useState, useEffect } from 'react';
import { 
  Save, Eye, Copy, Check, Loader2, Globe, Gift, 
  Link, Type, MessageSquare, Sparkles, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useCMS } from './CMSContext';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'da', name: 'Danish', flag: '🇩🇰' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
];

const DEFAULT_CONTENT = {
  title: "Welcome to",
  brand: "Freestays",
  subtitle: "Exclusive Offer Just for You",
  headline: "FREE HOTEL NIGHTS",
  description: "Book with meal packages & your room is",
  freeWord: "FREE",
  benefit1: "450,000+ hotels worldwide",
  benefit2: "No booking fees with Freestays Pass",
  benefit3: "Save up to 50% on every stay",
  geoText: "destinations ready in",
  geoLink: "/",
  ctaText: "Start Exploring Now!",
  ctaLink: "/",
  loadingText: "Preparing your experience...",
  detectingText: "Detecting your location...",
  loadingDealsText: "Loading best deals for you...",
  preparingText: "Preparing hotel search...",
  readyText: "Ready!",
  pleaseWaitText: "Please wait...",
  loadingPersonalizedText: "Loading your personalized experience..."
};

const CMSPopupSettings = ({ apiUrl, adminToken }) => {
  // Support both CMS context token and direct adminToken prop
  let cmsToken = null;
  try {
    const cms = useCMS();
    cmsToken = cms?.token;
  } catch (e) {
    // Not in CMS context, use adminToken prop
  }
  const token = adminToken || cmsToken;
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('en');
  const [showPreview, setShowPreview] = useState(false);
  
  // Fetch settings on mount
  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token]);
  
  const fetchSettings = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${apiUrl}/api/admin/popup/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      toast.error('Failed to load popup settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/popup/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: settings.enabled,
          autoCloseSeconds: settings.autoCloseSeconds,
          showFrequencyHours: settings.showFrequencyHours
        })
      });
      
      if (response.ok) {
        toast.success('Global settings saved');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const saveLanguageContent = async (language) => {
    setSaving(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/popup/content/${language}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings.content[language])
      });
      
      if (response.ok) {
        const langName = LANGUAGES.find(l => l.code === language)?.name || language;
        toast.success(`${langName} content saved`);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save language content');
    } finally {
      setSaving(false);
    }
  };
  
  const copyToAllLanguages = async () => {
    if (!window.confirm(`Copy ${LANGUAGES.find(l => l.code === activeLanguage)?.name} content to ALL other languages? This will overwrite existing translations.`)) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/popup/copy-to-all/${activeLanguage}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Content copied to all languages');
        fetchSettings(); // Reload to show changes
      } else {
        throw new Error('Failed to copy');
      }
    } catch (error) {
      toast.error('Failed to copy content');
    } finally {
      setSaving(false);
    }
  };
  
  const updateContent = (field, value) => {
    setSettings(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [activeLanguage]: {
          ...prev.content[activeLanguage],
          [field]: value
        }
      }
    }));
  };
  
  const getCurrentContent = () => {
    return settings?.content?.[activeLanguage] || DEFAULT_CONTENT;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  const content = getCurrentContent();
  
  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift className="w-6 h-6 text-amber-500" />
            Warmup Popup Settings
          </h1>
          <p className="text-gray-600 mt-1">
            Configure the welcome popup shown to visitors on first visit
          </p>
        </div>
        <button
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>
      
      {/* Global Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Globe size={20} />
          Global Settings
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings?.enabled ?? true}
              onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
              className="w-5 h-5 rounded"
            />
            <span className="font-medium">Enable Popup</span>
          </label>
          
          <div className="p-3 bg-gray-50 rounded-lg">
            <label className="block text-sm text-gray-600 mb-1">Auto-close (seconds)</label>
            <input
              type="number"
              min="0"
              max="30"
              value={settings?.autoCloseSeconds ?? 5}
              onChange={(e) => setSettings(prev => ({ ...prev, autoCloseSeconds: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">0 = manual close only</p>
          </div>
          
          <div className="p-3 bg-gray-50 rounded-lg">
            <label className="block text-sm text-gray-600 mb-1">Show again after (hours)</label>
            <input
              type="number"
              min="1"
              max="720"
              value={settings?.showFrequencyHours ?? 24}
              onChange={(e) => setSettings(prev => ({ ...prev, showFrequencyHours: parseInt(e.target.value) || 24 }))}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Save Global
            </button>
          </div>
        </div>
      </div>
      
      {/* Language Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <div className="flex">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setActiveLanguage(lang.code)}
                className={`px-4 py-3 font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeLanguage === lang.code
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{lang.flag}</span>
                {lang.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content Editor */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {LANGUAGES.find(l => l.code === activeLanguage)?.flag} {LANGUAGES.find(l => l.code === activeLanguage)?.name} Content
            </h3>
            <div className="flex gap-2">
              <button
                onClick={copyToAllLanguages}
                disabled={saving}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Copy size={16} />
                Copy to All Languages
              </button>
              <button
                onClick={() => saveLanguageContent(activeLanguage)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save {LANGUAGES.find(l => l.code === activeLanguage)?.name}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Main Content */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-700 flex items-center gap-2">
                <Type size={16} />
                Main Content
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Title</label>
                  <input
                    type="text"
                    value={content.title || ''}
                    onChange={(e) => updateContent('title', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Welcome to"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Brand Name</label>
                  <input
                    type="text"
                    value={content.brand || ''}
                    onChange={(e) => updateContent('brand', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Freestays"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-600 mb-1">Subtitle</label>
                <input
                  type="text"
                  value={content.subtitle || ''}
                  onChange={(e) => updateContent('subtitle', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Exclusive Offer Just for You"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-600 mb-1">Headline (Big Text)</label>
                <input
                  type="text"
                  value={content.headline || ''}
                  onChange={(e) => updateContent('headline', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-lg font-bold"
                  placeholder="FREE HOTEL NIGHTS"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">Description</label>
                  <input
                    type="text"
                    value={content.description || ''}
                    onChange={(e) => updateContent('description', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Book with meal packages & your room is"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">"Free" Word</label>
                  <input
                    type="text"
                    value={content.freeWord || ''}
                    onChange={(e) => updateContent('freeWord', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg font-bold text-amber-600"
                    placeholder="FREE"
                  />
                </div>
              </div>
              
              <h4 className="font-medium text-gray-700 flex items-center gap-2 pt-4">
                <Check size={16} />
                Benefits (3 Checkmarks)
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Benefit 1</label>
                  <input
                    type="text"
                    value={content.benefit1 || ''}
                    onChange={(e) => updateContent('benefit1', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="450,000+ hotels worldwide"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Benefit 2</label>
                  <input
                    type="text"
                    value={content.benefit2 || ''}
                    onChange={(e) => updateContent('benefit2', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="No booking fees with Freestays Pass"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Benefit 3</label>
                  <input
                    type="text"
                    value={content.benefit3 || ''}
                    onChange={(e) => updateContent('benefit3', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Save up to 50% on every stay"
                  />
                </div>
              </div>
            </div>
            
            {/* Right Column - Links & Loading */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-700 flex items-center gap-2">
                <Link size={16} />
                Links & CTAs
              </h4>
              
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                <p className="text-sm font-medium text-amber-800">Geo Message Link (Country destinations)</p>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Text before country name</label>
                  <input
                    type="text"
                    value={content.geoText || ''}
                    onChange={(e) => updateContent('geoText', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="destinations ready in"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Link URL</label>
                  <input
                    type="text"
                    value={content.geoLink || ''}
                    onChange={(e) => updateContent('geoLink', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                    placeholder="/search?country={country_id}"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variables: {'{country}'}, {'{country_id}'}, {'{language}'}</p>
                </div>
              </div>
              
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                <p className="text-sm font-medium text-green-800">CTA Button</p>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={content.ctaText || ''}
                    onChange={(e) => updateContent('ctaText', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg font-bold"
                    placeholder="Start Exploring Now!"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Button Link URL</label>
                  <input
                    type="text"
                    value={content.ctaLink || ''}
                    onChange={(e) => updateContent('ctaLink', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                    placeholder="/pricing"
                  />
                </div>
              </div>
              
              <h4 className="font-medium text-gray-700 flex items-center gap-2 pt-4">
                <MessageSquare size={16} />
                Loading Messages
              </h4>
              
              <div className="space-y-3 text-sm">
                <div>
                  <label className="block text-gray-600 mb-1">Loading text</label>
                  <input
                    type="text"
                    value={content.loadingText || ''}
                    onChange={(e) => updateContent('loadingText', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">Detecting location</label>
                  <input
                    type="text"
                    value={content.detectingText || ''}
                    onChange={(e) => updateContent('detectingText', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">Loading deals</label>
                  <input
                    type="text"
                    value={content.loadingDealsText || ''}
                    onChange={(e) => updateContent('loadingDealsText', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">Preparing search</label>
                  <input
                    type="text"
                    value={content.preparingText || ''}
                    onChange={(e) => updateContent('preparingText', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-600 mb-1">Ready!</label>
                    <input
                      type="text"
                      value={content.readyText || ''}
                      onChange={(e) => updateContent('readyText', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 mb-1">Please wait</label>
                    <input
                      type="text"
                      value={content.pleaseWaitText || ''}
                      onChange={(e) => updateContent('pleaseWaitText', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Preview Modal */}
      {showPreview && (
        <PopupPreview 
          content={content}
          onClose={() => setShowPreview(false)}
          autoCloseSeconds={settings?.autoCloseSeconds || 5}
        />
      )}
    </div>
  );
};

// Preview Component
const PopupPreview = ({ content, onClose, autoCloseSeconds }) => {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-amber-500/20 overflow-hidden">
        
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />
        
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
          type="button"
        >
          <X size={20} />
        </button>
        
        <div className="relative p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 shadow-lg shadow-amber-500/30">
            <Gift size={36} className="text-white" />
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {content.title} <span className="text-amber-400">{content.brand}</span>
          </h2>
          
          <p className="text-amber-200/80 mb-6 flex items-center justify-center gap-2">
            <Sparkles size={16} className="text-amber-400" />
            {content.subtitle}
            <Sparkles size={16} className="text-amber-400" />
          </p>
          
          <div className="bg-white/5 backdrop-blur rounded-xl p-6 mb-6 border border-amber-500/20">
            <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300 mb-2">
              {content.headline}
            </div>
            <p className="text-white/80 text-lg mb-4">
              {content.description} <span className="text-amber-400 font-bold">{content.freeWord}</span>
            </p>
            
            <div className="space-y-2 text-left max-w-xs mx-auto">
              <div className="flex items-center gap-2 text-white/90">
                <Check size={18} className="text-green-400 flex-shrink-0" />
                <span>{content.benefit1}</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Check size={18} className="text-green-400 flex-shrink-0" />
                <span>{content.benefit2}</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Check size={18} className="text-green-400 flex-shrink-0" />
                <span>{content.benefit3}</span>
              </div>
            </div>
          </div>
          
          <p className="text-amber-300/70 text-sm mb-4 underline cursor-pointer hover:text-amber-300">
            🌍 2+ {content.geoText} Netherlands
          </p>
          
          <div className="flex items-center justify-center gap-2 text-sm mb-4">
            <Check size={16} className="text-green-400" />
            <span className="text-green-400">{content.readyText}</span>
          </div>
          
          <button className="w-full py-4 px-8 rounded-xl font-bold text-lg bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 shadow-lg">
            🎉 {content.ctaText}
          </button>
          
          <p className="mt-4 text-white/40 text-xs">
            Auto-closes in {autoCloseSeconds} seconds | This is a preview
          </p>
        </div>
      </div>
    </div>
  );
};

export default CMSPopupSettings;
